import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { animated, useSpring } from '@react-spring/web'
import { parsePhoneNumber } from 'libphonenumber-js'
import { Form, Link, data, redirect, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { AuthFormWrapper } from '#app/components/auth/AuthFormWrapper.js'
import { AuthHeader } from '#app/components/auth/AuthHeader.js'
import { AuthLayout } from '#app/components/auth/AuthLayout.js'
import { AuthProviders } from '#app/components/auth/AuthProviders.js'
import { OtpEmail } from '#app/components/emails/OtpEmail'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import {
	ErrorList,
	FloatingLabelField,
	FloatingPhoneInputField,
} from '#app/components/forms'
import { AnimatedContainer } from '#app/components/ui/animated-container'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { StaggerChildren } from '#app/components/ui/stagger-children'
import { smoothSpring } from '#app/utils/animations'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { requireAnonymous } from '#app/utils/auth.server'
import { capitalizeWords } from '#app/utils/capitalize'
import { db } from '#app/utils/db.server'
import { sendEmail } from '#app/utils/email.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { getEmailBaseUrl, useIsPending } from '#app/utils/misc'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import { verifySessionStorage } from '#app/utils/verification.server'
import type { Route } from './+types/tenant.signup'
import { prepareVerification } from './verify.server'

interface SEOHandle {
	getSitemapEntries: () => null
}

export const handle = {
	getSitemapEntries: () => null,
} satisfies SEOHandle

const PhoneSchema = z
	.string({ required_error: 'Phone number is required' })
	.min(1, { message: 'Phone number is required' })
	.refine(
		(phone) => {
			if (!phone || phone.trim() === '') {
				return false
			}
			try {
				const parsed = parsePhoneNumber(phone)
				return parsed?.isValid() ?? false
			} catch {
				try {
					const parsed = parsePhoneNumber(phone, 'CA')
					return parsed?.isValid() ?? false
				} catch {
					return false
				}
			}
		},
		{
			message: 'Please enter a valid phone number',
		},
	)

const FirstNameSchema = z
	.string({ required_error: 'First name is required' })
	.min(1, { message: 'First name is required' })
	.max(50, { message: 'First name is too long' })
	// Title-case so the stored name has a capital first letter regardless of typing.
	.transform((value) => capitalizeWords(value))

const LastNameSchema = z
	.string({ required_error: 'Last name is required' })
	.min(1, { message: 'Last name is required' })
	.max(50, { message: 'Last name is too long' })
	.transform((value) => capitalizeWords(value))

const SignupSchema = z
	.object({
		firstname: FirstNameSchema,
		lastname: LastNameSchema,
		email: EmailSchema,
		phone: PhoneSchema,
		password: PasswordSchema,
		confirmPassword: PasswordSchema,
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('tenant')
	const url = new URL(request.url)
	const invitation = url.searchParams.get('invitation')
	return { authConfig, invitation }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await db.query.user.findFirst({
				columns: { id: true },
				where: (user, { eq }) => eq(user.email, data.email),
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{
				status: 500,
			},
		)
	}
	const { email, firstname, lastname, phone, password } = submission.value
	const redirectToValue = formData.get('redirectTo')?.toString()
	const {
		redirectTo,
		otp,
		verifyUrl: _verifyUrl,
	} = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
		redirectTo: redirectToValue,
	})

	// Store password and user data in verification session
	const verifySession = await verifySessionStorage.getSession()
	verifySession.set('onboardingPassword', password)
	verifySession.set('onboardingFirstName', firstname)
	verifySession.set('onboardingLastName', lastname)
	verifySession.set('onboardingPhone', phone)
	verifySession.set('onboardingUserType', 'tenant') // Store userType for tenant

	// Store invitation token if present
	const invitationToken = formData.get('invitation')?.toString()
	if (invitationToken) {
		verifySession.set('onboardingInvitation', invitationToken)
	}

	// Store redirectTo if present (for post-onboarding redirect)
	if (redirectToValue) {
		verifySession.set('onboardingRedirectTo', redirectToValue)
	}

	// Send email with OTP (optional - don't block if it fails)
	try {
		const response = await sendEmail({
			to: email,
			subject: 'Welcome to TrueTenant!',
			react: (
				<OtpEmail
					otp={otp}
					baseUrl={getEmailBaseUrl(request)}
					type="confirm-email"
				/>
			),
		})
		if (response.status === 'error') {
			// Log error but don't block signup
			console.error('Failed to send verification email:', response.error)
		}
	} catch (error) {
		// Log error but don't block signup
		console.error('Error sending verification email:', error)
	}

	// Data is stored in session - no need to add to URL for security
	// URL params removed for security - session data will be used instead

	return redirect(redirectTo.toString(), {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Sign Up | TrueTenant - Tenant' }]
}

export default function TenantSignupRoute({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const invitation = loaderData.invitation || searchParams.get('invitation')
	const authConfig = loaderData.authConfig

	// Build login link preserving redirectTo and invitation params
	const loginParams = new URLSearchParams()
	if (redirectTo) loginParams.set('redirectTo', redirectTo)
	if (invitation) loginParams.set('invitation', invitation)
	const loginLink = loginParams.toString()
		? `${authConfig.loginPath}?${loginParams.toString()}`
		: authConfig.loginPath

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	// React Spring animation for error messages
	const hasErrors = Boolean(form.errors?.length)
	const errorSpring = useSpring({
		opacity: hasErrors ? 1 : 0,
		y: hasErrors ? 0 : -10,
		config: smoothSpring,
	})

	return (
		<AuthLayout
			backgroundImage={authConfig.backgroundImage}
			heroText={authConfig.heroText}
			userType="tenant"
		>
			<AuthHeader />
			<AuthFormWrapper
				title={authConfig.titles.signup}
				subtitle={authConfig.subtitles.signup}
				titleClassName="text-[#32302F] text-[20px] md:text-[35px] capitalize font-medium"
				subtitleClassName="text-[#747474] font-400 text-[18px] md:text-[20px]"
			>
				<AnimatedContainer>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						{invitation && (
							<input type="hidden" name="invitation" value={invitation} />
						)}
						{redirectTo && (
							<input type="hidden" name="redirectTo" value={redirectTo} />
						)}
						<StaggerChildren>
							<div className="mb-[20px] grid grid-cols-1 gap-[20px] md:grid-cols-2">
								<FloatingLabelField
									inputProps={{
										...getInputProps(fields.firstname, { type: 'text' }),
										// autoFocus: true,
										autoComplete: 'given-name',
										placeholder: 'Enter Your First Name',
										// Title-case as the user types (caret preserved — length
										// is unchanged), matching the screening name fields.
										onChange: (e) => {
											const el = e.currentTarget
											const pos = el.selectionStart
											el.value = capitalizeWords(el.value)
											if (pos !== null) el.setSelectionRange(pos, pos)
										},
										className:
											'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
									}}
									labelProps={{
										children: 'First Name',
										htmlFor: fields.firstname.id,
										className: 'text-[16px] text-[#32302F]',
									}}
									errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
									errors={fields.firstname.errors}
								/>

								<FloatingLabelField
									inputProps={{
										...getInputProps(fields.lastname, { type: 'text' }),
										autoComplete: 'family-name',
										placeholder: 'Enter Your Last Name',
										onChange: (e) => {
											const el = e.currentTarget
											const pos = el.selectionStart
											el.value = capitalizeWords(el.value)
											if (pos !== null) el.setSelectionRange(pos, pos)
										},
										className:
											'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
									}}
									labelProps={{
										children: 'Last Name',
										htmlFor: fields.lastname.id,
										className: 'text-[16px] text-[#32302F]',
									}}
									errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
									errors={fields.lastname.errors}
								/>
							</div>

							<FloatingLabelField
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									autoComplete: 'email',
									placeholder: 'Enter Your Email Address',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[30px]',
								}}
								labelProps={{
									children: 'Email Address',
									htmlFor: fields.email.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0 md:pb-0 '}
								className="mb-[20px]"
								errors={fields.email.errors}
							/>
							<FloatingPhoneInputField
								inputProps={{
									...getInputProps(fields.phone, { type: 'text' }),
									defaultCountry: 'CA',
									placeholder: '',
									className: '',
								}}
								labelProps={{
									children: 'Phone Number',
									className: 'text-[#32302F]',
								}}
								errors={fields.phone.errors}
								className="mb-[20px]"
								errorClass="md:px-0 md:pt-0 md:pb-0"
							/>

							<FloatingLabelField
								passwordHide
								inputProps={{
									...getInputProps(fields.password, { type: 'password' }),
									autoComplete: 'new-password',
									placeholder: 'Enter Password',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] border border-[#CCCCCC] rounded-[30px] text-[#484848]',
								}}
								labelProps={{
									children: 'Password',
									htmlFor: fields.password.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass="min-h-auto md:px-0 md:pt-0 md:pb-0"
								className="mb-[20px]"
								errors={fields.password.errors}
							/>
							<FloatingLabelField
								passwordHide
								inputProps={{
									...getInputProps(fields.confirmPassword, {
										type: 'password',
									}),
									autoComplete: 'new-password',
									placeholder: 'Confirm Password',
									className:
										'placeholder:text-[#CCCCCC] placeholder:text-[16px] text-[16px] px-[33px] py-[24px] border border-[#CCCCCC] rounded-[30px] text-[#484848]',
								}}
								labelProps={{
									children: 'Confirm Password',
									htmlFor: fields.confirmPassword.id,
									className: 'text-[16px] text-[#32302F]',
								}}
								errorClass={'min-h-auto md:px-0 md:pt-0 '}
								errors={fields.confirmPassword.errors}
							/>
							<animated.div style={errorSpring} className="mt-6 mb-2">
								<ErrorList errors={form.errors} id={form.errorId} />
							</animated.div>
							<AnimatedStatusButton
								className="mt-[32px] mb-[20px] h-[55px] w-full rounded-[30px] border-0 bg-[#32302F] py-[23px] text-[20px] text-[#FFFFFF] capitalize transition-colors duration-200 hover:bg-[#1a1918] hover:text-[#FFFFFF] active:bg-[#0f0f0e]"
								status={isPending ? 'pending' : (form.status ?? 'idle')}
								type="submit"
								disabled={isPending}
								variant={'outline'}
							>
								Create Account
							</AnimatedStatusButton>
							<p className="mt-5 mb-0 text-center text-[16px] font-normal text-[#32302F]">
								Already have an account?
								<span
									className="text-[16px] font-normal text-[#32302F]"
									aria-hidden
								>
									{' '}
								</span>{' '}
								<Link
									to={loginLink}
									className="text-md font-500 cursor-pointer text-[16px] font-medium text-black underline"
								>
									Log in
								</Link>
							</p>
						</StaggerChildren>
					</Form>
					<AnimatedContainer delay={0.3}>
						<AuthProviders
							type="Continue"
							redirectTo={redirectTo}
							userType="tenant"
						/>
					</AnimatedContainer>
				</AnimatedContainer>
			</AuthFormWrapper>
		</AuthLayout>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
